import { Divider, Menu, MenuItem, MenuProps, useMediaQuery } from "@mui/material";
import classNames from "classnames";
import { t } from "i18next";
import { ReactNode } from "react";
import { IconButtonComponent } from "../../buttons/button.module";
import { TypographyComponent } from "../../typography/typography.component";
import { ArrowLeftIcon } from "../../../../icons/arrow-left.icon";
import { CheckmarkIcon } from "../../../../icons/checkmark.icon";
import styles from "./menu-single-select.styles.module.scss";
import { PlainTooltipComponent } from "../../tooltips/plain-tooltip/plain-tooltip.component";

type MenuItemVariant = "default" | "destructive";
type MenuPosition = "left" | "bottom" | "top";

const MENU_POSITION_MAP: Record<MenuPosition, {
  anchorOrigin: MenuProps["anchorOrigin"];
  transformOrigin: MenuProps["transformOrigin"];
}> = {
  left: {
    anchorOrigin: { vertical: "top", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  },
  bottom: {
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  },
  top: {
    anchorOrigin: { vertical: "top", horizontal: "right" },
    transformOrigin: { vertical: "bottom", horizontal: "right" },
  },
};

export type MenuSingleSelectItem = { id?: string | number } & (
  | {
    type: "action",
    label: ReactNode | string,
    disabled?: boolean,
    icon?: ReactNode | string,
    variant?: MenuItemVariant,
    tooltip?: { isActive: boolean; label: ReactNode | string };
    selected?: boolean;
  }
  | { type: "divider"; }
  | { type: "header"; label: string; }
  | { type: "subheader"; label: string; }
);

type MenuSingleSelectComponentProps = {
  anchorEl: HTMLElement | null;
  items: MenuSingleSelectItem[];
  menuPosition?: MenuPosition;
  menuProps?: Omit<MenuProps, "anchorEl" | "classes" | "onClose" | "open">;
  onClose?: () => void;
  onItemClick?: (index: number, item: MenuSingleSelectItem) => void;
  open?: boolean;
  rootClassName?: string;
  footer?: ReactNode | null;
};

export const MenuSingleSelectComponent = ({
  anchorEl = null,
  items,
  menuPosition = "bottom",
  menuProps = {},
  onClose = () => {},
  onItemClick = () => {},
  open = false,
  rootClassName = "",
  footer = null,
}: MenuSingleSelectComponentProps) => {
  const isMobile = useMediaQuery("(max-width:375px)");
  const { anchorOrigin, transformOrigin } = MENU_POSITION_MAP[menuPosition];

  const handleClose = () => {
    onClose();
  };

  const handleItemClick = (index: number, item: MenuSingleSelectItem) => {
    if (item.type === "action" && item.selected) {
      return;
    }
    onItemClick(index, item);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      anchorReference={isMobile ? "none" : "anchorEl"}
      classes={{
        list: classNames(styles.menuList, footer && styles.menuListWithFooter),
        paper: classNames(
          styles.menuPaper,
          menuPosition === "bottom" && styles.menuPaperBottom,
          menuPosition === "left" && styles.menuPaperLeft,
          menuPosition === "top" && styles.menuPaperTop,
        ),
        root: classNames(styles.menuRoot, rootClassName),
      }}
      onClose={handleClose}
      open={open}
      transformOrigin={transformOrigin}
      {...menuProps}
    >
      {isMobile && (
        <div>
          <div className={styles.mobileHeader}>
            <IconButtonComponent
              ariaLabel={t("Back")}
              onClick={handleClose}
              size="small"
            >
              <ArrowLeftIcon />
            </IconButtonComponent>
            <TypographyComponent color="grey7" styling="metadata-default">
              {t("Back")}
            </TypographyComponent>
          </div>
          <Divider classes={{ root: classNames(styles.menuDivider, styles.mobileHeaderDivider) }} component="li" />
        </div>
      )}
      {items.map((item, index) => {
        if (item.type === "divider") {
          return <Divider classes={{ root: styles.menuDivider }} component="li" key={`divider-${index}`} />;
        } else if (item.type === "header") {
          return (
            <li key={`header-${index}`} className={styles.menuHeader}>
              <TypographyComponent color="grey8" boldness="semi" element="p" styling="metadata-lg">
                {item.label}
              </TypographyComponent>
            </li>
          );
        } else if (item.type === "subheader") {
          return (
            <li key={`subheader-${index}`} className={styles.menuSubheader}>
              <TypographyComponent color="grey6" boldness="semi" element="p" styling="metadata-xs">
                {item.label}
              </TypographyComponent>
            </li>
          );
        } else if (item.type === "action") {
          const isBeforeDivider = items[index + 1]?.type === "divider";
          const isAfterDivider = items[index - 1]?.type === "divider";
          const isFirstItem = index === 0;
          const isLastItem = index === items.length - 1;

          return (
            <PlainTooltipComponent
              key={`tooltip-${item.id ?? index}`}
              title={item.tooltip?.label}
              deactivate={Boolean(!item.tooltip?.isActive)}
            >
              <MenuItem
                classes={{
                  root: classNames(styles.menuItem, {
                    [styles.disabled]: item.disabled,
                    [styles.itemBeforeDivider]: isBeforeDivider,
                    [styles.itemAfterDivider]: isAfterDivider,
                    [styles.menuItemFirst]: isFirstItem,
                    [styles.menuItemLast]: isLastItem,
                    [styles.selected]: item.selected,
                  }),
                }}
                disabled={item.disabled}
                key={item.id ?? index}
                onClick={() => handleItemClick(index, item)}
                TouchRippleProps={{
                  classes: {
                    root: styles.menuItemRippleRoot,
                    child: styles.menuItemRippleChild,
                  },
                }}
              >
                {item.icon && (
                  typeof item.icon === "string" ?
                    <img className={styles.menuItemIcon} src={item.icon} alt={item.label as string} />
                    :
                    <span
                      className={
                        classNames(
                          styles.menuItemIcon,
                          { [styles.destructiveIcon]: item.variant === "destructive" }
                        )}
                    >
                      {item.icon}
                    </span>
                )}
                {typeof item.label === "string" ? (
                  <TypographyComponent color={item.variant === "destructive" ? "error-text" : "grey8"} element="p" styling="metadata-lg">
                    {item.label}
                  </TypographyComponent>
                ) : item.label}
                {item.selected && (
                  <span className={styles.selectedIcon}>
                    <CheckmarkIcon />
                  </span>
                )}
              </MenuItem>
            </PlainTooltipComponent>
          );
        }
        return null;
      })}
      {footer}
    </Menu>
  );
};
